import React, { useEffect, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import languageContext from "../context/language/languageContext";
import Navbar from "../components/Navbar";
import { Clock, Stethoscope, Baby, Loader } from "lucide-react";

const ChatHistoryDoctor = () => {
  const location = useLocation();
  const { setSessionTimeout, setAlert, setMessage } = useContext(languageContext);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false); // ✅ Loader state

  const fetchDoctorChatHistory = async () => {
    const patient_id = location.state?.patient_id;
    if (!patient_id) return;

    setLoading(true); // ✅ Start loading

    try {
      const response = await axios.post(
        "https://www.careplus.gopillz.com/doctor_chat_history",
        JSON.stringify({ id: patient_id }),
        {
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setMessages(response.data.chat_history || []);
      }
    } catch (error) {
      if (error.response?.data?.detail === "Token has expired") {
        setSessionTimeout(true);
      } else {
        console.error("Error fetching chat:", error.message);
        setAlert(true);
        setMessage("Please try again later.");
      }
    } finally {
      setLoading(false); // ✅ Stop loading
    }
  };

  useEffect(() => {
    fetchDoctorChatHistory();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-tr from-blue-50 to-indigo-100">
      <Navbar />
      <div className="max-w-5xl mx-auto pt-24 pb-12 px-4">
        <h2 className="text-3xl sm:text-4xl font-bold text-center text-indigo-900 mb-10">
          🩺 Child's Chat with Doctor
        </h2>

        {/* ✅ Show loader if loading */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader className="w-8 h-8 animate-spin text-indigo-600" />
            <span className="ml-3 text-indigo-600 font-medium text-lg">Loading chat history...</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 italic py-20">
            No doctor chat history found for your child.
          </div>
        ) : (
          <div className="space-y-6">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className="bg-white border border-indigo-200 rounded-xl shadow-md p-6 transition-all duration-200 hover:shadow-lg"
              >
                <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-600" />
                    <span>{new Date(msg.timestamp).toLocaleString()}</span>
                  </div>
                  <span className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-xs font-semibold">
                    Chat ID: {msg.id}
                  </span>
                </div>

                <div className="space-y-4 mt-2">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-700 font-semibold mb-1 flex items-center gap-1">
                      <Baby className="w-4 h-4" /> Child:
                    </p>
                    <p className="text-gray-800 leading-relaxed">
                      {msg.user_question || <em className="text-gray-400">[No Question]</em>}
                    </p>
                  </div>

                  <div className="bg-white border border-indigo-100 rounded-lg p-4">
                    <p className="text-sm text-indigo-700 font-semibold mb-1 flex items-center gap-1">
                      <Stethoscope className="w-4 h-4" /> Doctor:
                    </p>
                    <p className="text-gray-800 leading-relaxed">
                      {msg.bot_response || <em className="text-gray-400">[No Response]</em>}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatHistoryDoctor;
